import { DependencyList, RefObject, useEffect, useRef, useState } from 'react'

/**
 * Gives a fixed-height widget (e.g. a react-virtualized Table/Grid, which needs an explicit
 * pixel height) the height that fills the space between its own top and the bottom of the
 * viewport. Use it instead of hardcoding `100vh - <magic offset>`: those offsets are wrong as
 * soon as the content above the widget wraps onto more/less rows (e.g. filter chips).
 *
 * Attach the returned ref to an element sitting exactly where the widget starts (a zero-height
 * sentinel `<div>` works well). The height re-measures on window resize (rAF-throttled) and
 * whenever a value in `deps` changes — e.g. a tab switch or a row-count change that shifts the
 * widget down or up.
 *
 * Safe by construction against render loops:
 * - the measured value depends only on layout ABOVE the ref, never on the widget's own height,
 *   so applying the height cannot move the ref and re-trigger a measure;
 * - the state setter bails out (returns the previous value) when the height is unchanged, so a
 *   re-run that measures the same number produces no re-render.
 *
 * @param deps      values that, when changed, shift the widget's top and warrant a re-measure
 * @param bottomGap px to leave between the widget's bottom and the viewport edge
 */
export function useFillViewportHeight<T extends HTMLElement = HTMLDivElement>(
  deps: DependencyList = [],
  bottomGap = 32
): [RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [height, setHeight] = useState(0)

  useEffect(() => {
    let raf = 0

    const measure = (): void => {
      if (!ref.current) return
      const top = ref.current.getBoundingClientRect().top
      const next = Math.max(0, window.innerHeight - top - bottomGap)
      // Bail out when unchanged so an identical measurement never triggers a re-render.
      setHeight((prev) => (prev === next ? prev : next))
    }

    const onResize = (): void => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', onResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bottomGap, ...deps])

  return [ref, height]
}
